import React from 'react';
import {
  Box,
  Chip,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { brandColors } from '@config/brandTokens';
import { isReleaseSport } from '@config/releaseProfile';
import {
  ADMIN_NAV_TONES,
  adminChildNavButtonSx,
  adminTopNavButtonSx,
  type AdminNavReleaseTone,
} from './adminSidebarTheme';

export function AdminSidebarLegend({ collapsed }: { collapsed: boolean }) {
  if (collapsed) return null;
  return (
    <Box
      sx={{
        mx: 1.5,
        mb: 1,
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        bgcolor: brandColors.white,
        border: '1px solid #e2e8f0',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.68rem',
          fontWeight: 800,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: brandColors.slate,
          mb: 0.75,
        }}
      >
        Sidebar key
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: ADMIN_NAV_TONES.v1.border }} />
          <Typography sx={{ fontSize: '0.72rem', color: brandColors.navy, fontWeight: 600 }}>
            {ADMIN_NAV_TONES.v1.label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: ADMIN_NAV_TONES.future.border }} />
          <Typography sx={{ fontSize: '0.72rem', color: brandColors.slate, fontWeight: 500 }}>
            {ADMIN_NAV_TONES.future.label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function AdminSidebarSectionDivider({
  label,
  tone = 'future',
  collapsed,
}: {
  label: string;
  tone?: AdminNavReleaseTone;
  collapsed: boolean;
}) {
  if (collapsed) return null;
  const t = ADMIN_NAV_TONES[tone];
  return (
    <ListItem sx={{ py: 0.75, px: 2 }}>
      <Typography
        sx={{
          fontSize: '0.68rem',
          fontWeight: 800,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: t.color,
        }}
      >
        {label}
      </Typography>
    </ListItem>
  );
}

interface AdminSidebarNavButtonProps {
  label: string;
  icon: React.ReactNode;
  selected?: boolean;
  collapsed: boolean;
  tone?: AdminNavReleaseTone;
  accentColor?: string;
  secondary?: string;
  onClick: () => void;
  disabled?: boolean;
}

export function AdminSidebarNavButton({
  label,
  icon,
  selected = false,
  collapsed,
  tone = 'neutral',
  accentColor,
  secondary,
  onClick,
  disabled,
}: AdminSidebarNavButtonProps) {
  const t = ADMIN_NAV_TONES[tone];
  const color = accentColor || t.color;
  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={selected}
        disabled={disabled}
        onClick={onClick}
        sx={adminTopNavButtonSx(tone, selected, collapsed, accentColor)}
      >
        <ListItemIcon
          sx={{
            color: selected ? color : tone === 'future' ? brandColors.slate : brandColors.navy,
            minWidth: collapsed ? 0 : 44,
          }}
        >
          {icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={label}
            secondary={secondary}
            primaryTypographyProps={{
              fontSize: '0.88rem',
              fontWeight: selected ? 700 : 600,
              color: selected ? color : tone === 'future' ? brandColors.slate : brandColors.navy,
            }}
            secondaryTypographyProps={{ fontSize: '0.68rem', color: brandColors.slate }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
}

interface AdminSidebarChildButtonProps {
  label: string;
  selected?: boolean;
  collapsed: boolean;
  tone?: AdminNavReleaseTone;
  depth?: number;
  onClick?: () => void;
  disabled?: boolean;
  fontSize?: string;
}

export function AdminSidebarChildButton({
  label,
  selected = false,
  collapsed,
  tone = 'future',
  depth = 2,
  onClick,
  disabled,
  fontSize = '0.84rem',
}: AdminSidebarChildButtonProps) {
  const t = ADMIN_NAV_TONES[tone];
  return (
    <ListItemButton
      selected={selected}
      disabled={disabled}
      onClick={onClick}
      sx={adminChildNavButtonSx(tone, depth)}
    >
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize,
            fontWeight: selected ? 700 : tone === 'future' ? 500 : 600,
            color: selected ? t.color : tone === 'future' ? '#64748b' : brandColors.navy,
          }}
        />
      )}
    </ListItemButton>
  );
}

export function AdminSidebarSportLabel({
  sport,
  collapsed,
}: {
  sport: string;
  collapsed: boolean;
}) {
  if (collapsed) return null;
  const inRelease = isReleaseSport(sport);
  return (
    <ListItem sx={{ py: 0.35, pl: 2.5, pr: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 800,
            color: inRelease ? brandColors.navy : brandColors.slate,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            fontSize: '0.72rem',
          }}
        >
          {sport}
        </Typography>
        {inRelease ? (
          <Chip
            label="1.0 sport"
            size="small"
            sx={{
              height: 18,
              fontSize: '0.62rem',
              fontWeight: 800,
              bgcolor: 'rgba(232, 88, 11, 0.12)',
              color: brandColors.orange,
              border: `1px solid rgba(232, 88, 11, 0.35)`,
            }}
          />
        ) : (
          <Chip
            label="Future"
            size="small"
            sx={{
              height: 18,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: 'rgba(70, 80, 105, 0.08)',
              color: brandColors.slate,
            }}
          />
        )}
      </Box>
    </ListItem>
  );
}
